import React from 'react';
import styled from 'styled-components';
import { Body1, Body5, Body7, Container, Page } from '@ui';
import { FlatButton, SkeletonButton, TransparentFlatButton } from '@ui/buttons';
import BackArrowButton from '@ui/buttons/BackArrowButton';
import { Working } from '@ui/Working';
import Close from '@assets/icons/svgs/Close';
import {
  listBoxDocIngestionConfigs,
  syncBoxDocIngestionConfig,
  updateBoxDocIngestionConfigValidTags,
} from '@services/boxDocIngestionConfigs';

const ListWrapper = styled.div`
  border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  border-radius: 6px;
  overflow: hidden;
`;

const ConfigRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};

  &:last-child {
    border-bottom: none;
  }
`;

const ConfigDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
  width: 100%;
`;

const SyncActionsRow = styled.div`
  margin-top: 12px;

  button {
    min-width: 120px;
  }
`;

const ErrorText = styled(Body5)`
  color: ${({ theme }) => theme.themeColors.error};
`;

const TagsEditorWrapper = styled.div`
  width: 100%;
  margin-top: 12px;
`;

const TagsInputArea = styled.div`
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  color: ${({ theme }) => theme.themeColors.text};
  background-color: transparent;
  border-radius: 6px;
  width: 100%;
  padding: 8px;
  cursor: text;

  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 8px;

  input {
    font-size: 14px;
    border: none !important;
    padding: 4px 0 0 0 !important;
    outline: none;
    line-height: 18px;
    background: transparent;
    color: inherit;
    flex: 1 1 200px;
    min-width: 200px;
  }

  ${({ $disabled }) =>
    $disabled
      ? `
    opacity: 0.6;
    cursor: not-allowed;
    input { cursor: not-allowed; }
  `
      : ''}
`;

const TagChip = styled.div`
  border-radius: 2px;
  background: ${({ theme }) => theme.themeColors.primaryCtaButton};
  border: none;
  cursor: default;

  padding: 3px 6px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;

  * {
    color: ${({ theme }) => theme.themeColors.buttonText} !important;
    fill: ${({ theme }) => theme.themeColors.buttonText} !important;
  }

  svg {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    margin-left: 8px;
    height: 8px !important;
    width: 12px !important;
    opacity: 0.8;
  }
`;

const ChipRemoveButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  display: inline-flex;
  align-items: center;

  &:hover {
    opacity: ${({ $disabled }) => ($disabled ? 1 : 0.8)};
  }
`;

const TagsActionRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  margin-top: 8px;

  button {
    min-width: 96px;
  }
`;

const normalizeTags = (tags) => {
  const seen = new Set();
  const result = [];
  (tags || []).forEach((tag) => {
    if (typeof tag !== 'string') return;
    const trimmed = tag.trim();
    if (!trimmed) return;
    if (seen.has(trimmed)) return;
    seen.add(trimmed);
    result.push(trimmed);
  });
  return result;
};

const tagsEqual = (a, b) => {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

const getBoxConfigUserEmail = (config) => {
  const fromUser = config?.user && typeof config.user === 'object' ? config.user.email : null;
  return config?.user_email || config?.email || fromUser || '';
};

const getBoxFolderTitle = (config) => {
  if (config?.box_folder_name) return config.box_folder_name;
  if (config?.box_folder_id) return `Folder ${config.box_folder_id}`;
  return 'Unnamed folder';
};

function ValidTagsEditor({ config, canMutate, isSaving, error, onSave }) {
  const baselineTags = React.useMemo(() => config.valid_tags || [], [config.valid_tags]);
  const [draftTags, setDraftTags] = React.useState(baselineTags);
  const [inputValue, setInputValue] = React.useState('');
  const [localError, setLocalError] = React.useState(null);

  React.useEffect(() => {
    setDraftTags(baselineTags);
    setInputValue('');
    setLocalError(null);
  }, [baselineTags]);

  const isDirty = !tagsEqual(normalizeTags(draftTags), normalizeTags(baselineTags));
  const disabled = !canMutate || isSaving;

  const commitInputAsTag = React.useCallback(() => {
    const trimmed = inputValue.trim();
    if (!trimmed) {
      setInputValue('');
      return;
    }
    setLocalError(null);
    setDraftTags((prev) => {
      if (prev.some((t) => t === trimmed)) {
        return prev;
      }
      return [...prev, trimmed];
    });
    setInputValue('');
  }, [inputValue]);

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commitInputAsTag();
    } else if (e.key === 'Backspace' && inputValue === '' && draftTags.length > 0) {
      setDraftTags((prev) => prev.slice(0, -1));
    }
  };

  const handleRemoveTag = (tag) => {
    if (disabled) return;
    setLocalError(null);
    setDraftTags((prev) => prev.filter((t) => t !== tag));
  };

  const handleSave = () => {
    const nextTags = normalizeTags(draftTags);
    if (nextTags.length === 0 && config.auto_doc_ingestion_enabled) {
      setLocalError('Valid tags must contain at least one tag when auto_doc_ingestion_enabled is true');
      return;
    }
    setLocalError(null);
    onSave(nextTags);
  };

  const handleCancel = () => {
    setDraftTags(baselineTags);
    setInputValue('');
    setLocalError(null);
  };

  return (
    <TagsEditorWrapper>
      <Container bottom={6}>
        <Body7 isLowContrast>Valid tags</Body7>
      </Container>
      <TagsInputArea $disabled={disabled}>
        {draftTags.map((tag) => (
          <TagChip key={tag}>
            <Body5>{tag}</Body5>
            <ChipRemoveButton
              type="button"
              aria-label={`Remove tag ${tag}`}
              $disabled={disabled}
              disabled={disabled}
              onClick={() => handleRemoveTag(tag)}
            >
              <Close width={12} />
            </ChipRemoveButton>
          </TagChip>
        ))}
        <input
          type="text"
          value={inputValue}
          placeholder={canMutate ? 'Type a tag and press Enter' : ''}
          disabled={disabled}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleInputKeyDown}
          onBlur={() => {
            if (inputValue.trim()) {
              commitInputAsTag();
            }
          }}
        />
      </TagsInputArea>
      {(localError || error) && (
        <Container top={8}>
          <ErrorText>{localError || error}</ErrorText>
        </Container>
      )}
      {isDirty && (
        <TagsActionRow>
          <FlatButton disabled={disabled} onClick={handleSave}>
            {isSaving ? 'Saving...' : 'Save'}
          </FlatButton>
          <TransparentFlatButton disabled={disabled} onClick={handleCancel}>
            Cancel
          </TransparentFlatButton>
        </TagsActionRow>
      )}
    </TagsEditorWrapper>
  );
}

function BoxDocIngestionConfigRow({
  config,
  canMutate,
  isSubmitting,
  isSyncing,
  toggleError,
  syncError,
  onSaveTags,
  onSync,
}) {
  const rowBusy = isSubmitting || isSyncing;
  const ownerEmail = getBoxConfigUserEmail(config).trim();
  const title = getBoxFolderTitle(config);
  const showFolderIdLine = !!config.box_folder_name && !!config.box_folder_id;
  const hasFileLimits = config.max_files != null || config.max_docs != null;

  return (
    <ConfigRow>
      <ConfigDetails>
        <Body5 bold>{title}</Body5>
        {ownerEmail ? (
          <Container top={4}>
            <Body5 isLowContrast>Email: {ownerEmail}</Body5>
          </Container>
        ) : null}
        {config.box_user_id ? (
          <Container top={4}>
            <Body5 isLowContrast>Box user ID: {config.box_user_id}</Body5>
          </Container>
        ) : null}
        {showFolderIdLine ? (
          <Container top={4}>
            <Body5 isLowContrast>Folder ID: {config.box_folder_id}</Body5>
          </Container>
        ) : null}
        {hasFileLimits ? (
          <Container top={4}>
            <Body5 isLowContrast>
              {config.max_files != null ? `Max files: ${config.max_files}` : null}
              {config.max_files != null && config.max_docs != null ? ' • ' : null}
              {config.max_docs != null ? `Max docs: ${config.max_docs}` : null}
            </Body5>
          </Container>
        ) : null}

        <ValidTagsEditor
          config={config}
          canMutate={canMutate}
          isSaving={isSubmitting}
          error={toggleError}
          onSave={(nextTags) => onSaveTags(config, nextTags)}
        />

        {syncError && (
          <Container top={8}>
            <ErrorText>{syncError}</ErrorText>
          </Container>
        )}
        <SyncActionsRow>
          <SkeletonButton disabled={!canMutate || rowBusy} onClick={() => onSync(config)}>
            {isSyncing ? 'Syncing...' : 'Sync now'}
          </SkeletonButton>
        </SyncActionsRow>
        {!canMutate && (
          <Container top={8}>
            <Body5 isLowContrast>Only the owner can sync or edit tags for this folder.</Body5>
          </Container>
        )}
      </ConfigDetails>
    </ConfigRow>
  );
}

export function BoxDocIngestionUploader({ pageWidth, onGoBack }) {
  const [configs, setConfigs] = React.useState([]);
  const [listError, setListError] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [updatingConfigIds, setUpdatingConfigIds] = React.useState({});
  const [syncingConfigIds, setSyncingConfigIds] = React.useState({});
  const [rowErrors, setRowErrors] = React.useState({});
  const [syncRowErrors, setSyncRowErrors] = React.useState({});

  const fetchConfigs = React.useCallback(async (cancellationRef) => {
    setIsLoading(true);
    const { boxDocIngestionConfigs, error } = await listBoxDocIngestionConfigs();

    if (cancellationRef?.isCanceled) {
      return;
    }

    setConfigs(boxDocIngestionConfigs || []);
    setListError(error || null);
    setIsLoading(false);
  }, []);

  React.useEffect(() => {
    const cancellationRef = { isCanceled: false };
    fetchConfigs(cancellationRef);

    return () => {
      cancellationRef.isCanceled = true;
    };
  }, [fetchConfigs]);

  const handleSaveTags = React.useCallback(async (config, nextTags) => {
    setRowErrors((prev) => ({ ...prev, [config.id]: null }));
    setUpdatingConfigIds((prev) => ({ ...prev, [config.id]: true }));

    const { boxDocIngestionConfig, error } = await updateBoxDocIngestionConfigValidTags(config.id, nextTags);

    setUpdatingConfigIds((prev) => ({ ...prev, [config.id]: false }));

    if (boxDocIngestionConfig) {
      setConfigs((prev) => prev.map((row) => (row.id === config.id ? boxDocIngestionConfig : row)));
      return;
    }

    setRowErrors((prev) => ({
      ...prev,
      [config.id]: error || 'Failed to update config.',
    }));
  }, []);

  const handleSync = React.useCallback(async (config) => {
    setSyncRowErrors((prev) => ({ ...prev, [config.id]: null }));
    setSyncingConfigIds((prev) => ({ ...prev, [config.id]: true }));

    const { boxDocIngestionConfig, error } = await syncBoxDocIngestionConfig(config.id);

    setSyncingConfigIds((prev) => ({ ...prev, [config.id]: false }));

    if (boxDocIngestionConfig?.id != null) {
      setConfigs((prev) => prev.map((row) => (row.id === config.id ? boxDocIngestionConfig : row)));
      return;
    }

    if (error) {
      setSyncRowErrors((prev) => ({
        ...prev,
        [config.id]: error,
      }));
    }
  }, []);

  return (
    <Page width={pageWidth}>
      <BackArrowButton onClick={onGoBack} />
      <Container top={36}>
        <Body1 bold>Box Document Ingestion</Body1>
        <Container top={8}>
          <Body5 isLowContrast>View each configured Box folder.</Body5>
        </Container>

        {isLoading ? (
          <Container top={24}>
            <Working noVerticalPadding message="Loading configs" />
          </Container>
        ) : listError ? (
          <Container top={24}>
            <ErrorText>{listError}</ErrorText>
            <Container top={12}>
              <SkeletonButton onClick={() => fetchConfigs()}>Retry</SkeletonButton>
            </Container>
          </Container>
        ) : configs.length === 0 ? (
          <Container top={24}>
            <Body5 isLowContrast>No Box ingestion configs found.</Body5>
          </Container>
        ) : (
          <Container top={24}>
            <ListWrapper>
              {configs.map((config) => (
                <BoxDocIngestionConfigRow
                  key={config.id}
                  config={config}
                  canMutate={Boolean(config.is_owner)}
                  isSubmitting={!!updatingConfigIds[config.id]}
                  isSyncing={!!syncingConfigIds[config.id]}
                  toggleError={rowErrors[config.id]}
                  syncError={syncRowErrors[config.id]}
                  onSaveTags={handleSaveTags}
                  onSync={handleSync}
                />
              ))}
            </ListWrapper>
          </Container>
        )}
      </Container>
    </Page>
  );
}
